import { Sparkles } from 'lucide-react'
import { productName } from '../lib/config'
import './claude.css'

// The picture of how a Command Centre works (Sam, 10 Oct 2026): a wall of
// big screens with your views on them, and you at your own computer chatting
// to Claude Code. You ask at the desk, Claude does the work in your data, and
// the screens update. Drawn from the instance's own tab names and its own
// example question, so every customer sees their Command Centre, not a
// generic diagram.

const GLYPHS = ['bars', 'line', 'list', 'bars'] as const

function Glyph({ kind }: { kind: (typeof GLYPHS)[number] }) {
  if (kind === 'bars') {
    return (
      <span className="oc-mc-bars" aria-hidden>
        {[38, 62, 48, 80, 58, 92].map((h, i) => (
          <i key={i} style={{ height: `${h}%` }} />
        ))}
      </span>
    )
  }
  if (kind === 'line') {
    return (
      <svg className="oc-mc-line" viewBox="0 0 100 40" preserveAspectRatio="none" aria-hidden>
        <polyline points="0,32 16,26 32,29 48,17 64,20 80,9 100,12" fill="none" strokeWidth="2.5" />
        <polyline points="0,30 100,14" fill="none" strokeWidth="1.2" strokeDasharray="3 3" className="oc-mc-line-dim" />
      </svg>
    )
  }
  return (
    <span className="oc-mc-list" aria-hidden>
      {[86, 64, 72, 50].map((w, i) => (
        <i key={i}>
          <b />
          <em style={{ width: `${w}%` }} />
        </i>
      ))}
    </span>
  )
}

export function MissionControl({
  screens,
  example,
  compact = false,
}: {
  /** Tab names to put on the wall, in order (3 or 4 read best). */
  screens: string[]
  example: { ask: string; answer: string }
  compact?: boolean
}) {
  const wall = screens.slice(0, compact ? 3 : 4)
  return (
    <figure className={`oc-mc ${compact ? 'oc-mc-compact' : ''}`} aria-label={__FRONT_DOOR__ ? `${productName} and Claude working together` : 'The Command Centre screens and Claude working together'}>
      <div className="oc-mc-tag">
        <span className="oc-mc-dot" /> The screens: where you look
      </div>
      <div className="oc-mc-wall" style={{ gridTemplateColumns: `repeat(${wall.length}, minmax(0, 1fr))` }}>
        {wall.map((label, i) => (
          <div key={label} className="oc-mc-screen">
            <span className="oc-mc-screen-label">{label}</span>
            <Glyph kind={GLYPHS[i % GLYPHS.length]} />
          </div>
        ))}
      </div>

      <div className="oc-mc-flow" aria-hidden={compact}>
        <span>You ask</span>
        <i />
        <span>Claude works in your data</span>
        <i />
        <span>The screens update</span>
      </div>

      <div className="oc-mc-desk">
        <div className="oc-mc-laptop">
          <div className="oc-mc-bar">
            <span /> <span /> <span />
            <em>Claude Code</em>
          </div>
          <div className="oc-mc-chat">
            <p className="oc-mc-you">{example.ask}</p>
            <p className="oc-mc-claude">
              <Sparkles size={13} aria-hidden />
              <span>{example.answer}</span>
            </p>
          </div>
        </div>
        <div className="oc-mc-tag oc-mc-tag-desk">
          <span className="oc-mc-dot" /> Claude: where you ask, on your own computer
        </div>
      </div>
    </figure>
  )
}
